import { ScheduleAccountMode } from "@common/constants/domains/accounting/scheduleAccountFilters";
import {
    EXPENSE_SUB_MODE_LABELS,
    REVENUE_SUB_MODE_LABELS,
} from "@common/helpers/domains/accounting/scheduleApplicationFilters";
import {
    ExpenseSubMode,
    RecognitionAnchor,
    RevenueSubMode,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

/** One pick in the policy type picker: a schedule type with its sub-mode. */
export enum SchedulePolicyKind {
    deferredRevenue = "deferred_revenue",
    accruedRevenue = "accrued_revenue",
    prepaidExpense = "prepaid_expense",
    accruedExpense = "accrued_expense",
}

export const SCHEDULE_POLICY_KIND_MODES: Record<
    SchedulePolicyKind,
    ScheduleAccountMode
> = {
    [SchedulePolicyKind.deferredRevenue]: {
        type: ScheduleType.revenue,
        revenueSubMode: RevenueSubMode.deferred,
        expenseSubMode: null,
    },
    [SchedulePolicyKind.accruedRevenue]: {
        type: ScheduleType.revenue,
        revenueSubMode: RevenueSubMode.accrued,
        expenseSubMode: null,
    },
    [SchedulePolicyKind.prepaidExpense]: {
        type: ScheduleType.expense,
        revenueSubMode: null,
        expenseSubMode: ExpenseSubMode.prepaid,
    },
    [SchedulePolicyKind.accruedExpense]: {
        type: ScheduleType.expense,
        revenueSubMode: null,
        expenseSubMode: ExpenseSubMode.accrued,
    },
};

export const SCHEDULE_POLICY_KIND_LABELS: Record<SchedulePolicyKind, string> = {
    [SchedulePolicyKind.deferredRevenue]:
        REVENUE_SUB_MODE_LABELS[RevenueSubMode.deferred],
    [SchedulePolicyKind.accruedRevenue]:
        REVENUE_SUB_MODE_LABELS[RevenueSubMode.accrued],
    [SchedulePolicyKind.prepaidExpense]:
        EXPENSE_SUB_MODE_LABELS[ExpenseSubMode.prepaid],
    [SchedulePolicyKind.accruedExpense]:
        EXPENSE_SUB_MODE_LABELS[ExpenseSubMode.accrued],
};

export function getSchedulePolicyKindsForType(
    type: ScheduleType,
): SchedulePolicyKind[] {
    return Object.values(SchedulePolicyKind).filter(
        (kind) => SCHEDULE_POLICY_KIND_MODES[kind].type === type,
    );
}

export interface SchedulePolicyPeriodCopy {
    label: string;
    tooltip: string;
}

const RECOGNIZED_COPY: SchedulePolicyPeriodCopy = {
    label: "Recognized",
    tooltip: "Posted to the recognition account",
};

const AMORTIZED_COPY: SchedulePolicyPeriodCopy = {
    label: "Amortized",
    tooltip: "Posted to the expense account",
};

/** How a period's posting reads on a ladder: revenue recognizes, prepaid amortizes, accrued expense accrues. */
export const SCHEDULE_POLICY_KIND_PERIOD_COPY: Record<
    SchedulePolicyKind,
    SchedulePolicyPeriodCopy
> = {
    [SchedulePolicyKind.deferredRevenue]: RECOGNIZED_COPY,
    [SchedulePolicyKind.accruedRevenue]: RECOGNIZED_COPY,
    [SchedulePolicyKind.prepaidExpense]: AMORTIZED_COPY,
    [SchedulePolicyKind.accruedExpense]: {
        label: "Accrued",
        tooltip: "Posted to the expense account",
    },
};

/**
 * The anchor a new policy of this kind starts from. "Source date" is stored
 * as the transaction-date anchor and resolves from whatever the schedule is
 * applied to: on a bill, the bill date.
 */
export const SCHEDULE_POLICY_KIND_DEFAULT_ANCHORS: Record<
    SchedulePolicyKind,
    RecognitionAnchor
> = {
    [SchedulePolicyKind.deferredRevenue]: RecognitionAnchor.transactionDate,
    [SchedulePolicyKind.accruedRevenue]: RecognitionAnchor.transactionDate,
    [SchedulePolicyKind.prepaidExpense]: RecognitionAnchor.transactionDate,
    [SchedulePolicyKind.accruedExpense]: RecognitionAnchor.termStart,
};

export function toSchedulePolicyKind(
    mode: ScheduleAccountMode,
): SchedulePolicyKind | null {
    return (
        Object.values(SchedulePolicyKind).find((kind) => {
            const kindMode = SCHEDULE_POLICY_KIND_MODES[kind];
            return (
                kindMode.type === mode.type &&
                kindMode.revenueSubMode === mode.revenueSubMode &&
                kindMode.expenseSubMode === mode.expenseSubMode
            );
        }) ?? null
    );
}
